"use client";

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { ResponsivePopover } from "@west-tennessee-consulting/ui";
import { Badge } from "@/components/ui/badge";
import { Phone, Pencil } from "lucide-react";
import { useUpdateContact } from "@/features/contacts/hooks";
import { TooltipWithContent } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { normalizePhoneForInboundHub } from "@/lib/utils/crm/phone-match";
import {
  type PhoneAliasesEditable,
  normalizePhoneAliases,
} from "./phone-aliases-utils";
import { PhoneAliasesEditorPanel } from "./PhoneAliasesEditorPanel";

interface PhoneAliasesEditorProps<T extends PhoneAliasesEditable = PhoneAliasesEditable> {
  contact: T;
  onUpdate?: (updatedContact: T) => void;
  isEditing?: boolean;
  onEditChange?: (isEditing: boolean) => void;
  showEditorDirectly?: boolean;
  readOnlyMode?: boolean;
}

function phonesMatch(a: string, b: string): boolean {
  const na = normalizePhoneForInboundHub(a)?.replace(/\D/g, "") ?? a.replace(/\D/g, "");
  const nb = normalizePhoneForInboundHub(b)?.replace(/\D/g, "") ?? b.replace(/\D/g, "");
  if (na.length >= 10 && nb.length >= 10) {
    return na.slice(-10) === nb.slice(-10);
  }
  return na === nb;
}

export function PhoneAliasesEditor<T extends PhoneAliasesEditable = PhoneAliasesEditable>({
  contact,
  onUpdate,
  isEditing = false,
  onEditChange,
  showEditorDirectly = false,
  readOnlyMode = false,
}: PhoneAliasesEditorProps<T>) {
  const [isOpen, setIsOpen] = useState(isEditing || showEditorDirectly);
  const [newAlias, setNewAlias] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [localAliases, setLocalAliases] = useState<string[]>(
    normalizePhoneAliases(contact.phone_aliases)
  );
  const updateContact = useUpdateContact();

  const aliases = useMemo(() => localAliases, [localAliases]);
  const primaryPhone = contact.phone ?? "";

  useEffect(() => {
    setLocalAliases(normalizePhoneAliases(contact.phone_aliases));
  }, [contact.id, contact.phone_aliases]);

  const validatePhone = useCallback((value: string): string | null => {
    const normalized = normalizePhoneForInboundHub(value);
    if (!normalized) return "Enter a valid phone number";
    const digits = normalized.replace(/\D/g, "");
    if (digits.length < 10) return "Phone number is too short";
    return null;
  }, []);

  const handleAddAlias = useCallback(async () => {
    const trimmedAlias = newAlias.trim();
    if (!trimmedAlias) {
      setValidationError("Phone cannot be empty");
      return;
    }

    const phoneError = validatePhone(trimmedAlias);
    if (phoneError) {
      setValidationError(phoneError);
      return;
    }

    const normalized = normalizePhoneForInboundHub(trimmedAlias) ?? trimmedAlias;

    if (primaryPhone && phonesMatch(normalized, primaryPhone)) {
      setValidationError("This is already the primary phone");
      return;
    }

    if (aliases.some((a) => phonesMatch(a, normalized))) {
      setValidationError("This phone is already in the list");
      return;
    }

    setIsAdding(true);
    setValidationError(null);

    try {
      const updatedAliases = [...aliases, normalized];
      setLocalAliases(updatedAliases);
      setNewAlias("");

      if (!readOnlyMode) {
        await updateContact.mutateAsync({
          id: contact.id,
          updates: { phone_aliases: updatedAliases },
        });
      }
      toast.success("Phone alias added");

      onUpdate?.({ ...contact, phone_aliases: updatedAliases });
    } catch (error) {
      setLocalAliases(aliases);
      toast.error("Failed to add phone alias");
      setValidationError("Failed to add phone alias");
    } finally {
      setIsAdding(false);
    }
  }, [newAlias, contact, aliases, primaryPhone, validatePhone, updateContact, onUpdate, readOnlyMode]);

  const handleRemoveAlias = useCallback(
    async (aliasToRemove: string) => {
      try {
        const updatedAliases = aliases.filter((a) => a !== aliasToRemove);
        setLocalAliases(updatedAliases);

        if (!readOnlyMode) {
          await updateContact.mutateAsync({
            id: contact.id,
            updates: { phone_aliases: updatedAliases },
          });
        }
        toast.success("Phone alias removed");

        onUpdate?.({ ...contact, phone_aliases: updatedAliases });
      } catch {
        setLocalAliases(aliases);
        toast.error("Failed to remove phone alias");
      }
    },
    [contact, aliases, updateContact, onUpdate, readOnlyMode]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void handleAddAlias();
      }
    },
    [handleAddAlias]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onEditChange?.(open);
    },
    [onEditChange]
  );

  const panelProps = {
    showEditorDirectly,
    contactPhone: primaryPhone,
    aliases,
    newAlias,
    onAliasChange: (val: string) => {
      setNewAlias(val);
      setValidationError(null);
    },
    validationError,
    isAdding,
    isPending: updateContact.isPending,
    handleAddAlias,
    handleRemoveAlias,
    handleKeyPress,
    handleOpenChange,
  };

  if (showEditorDirectly) {
    return (
      <div className="space-y-4">
        <PhoneAliasesEditorPanel {...panelProps} />
      </div>
    );
  }

  return (
    <ResponsivePopover
      open={isOpen}
      onOpenChange={handleOpenChange}
      content={<PhoneAliasesEditorPanel {...panelProps} />}
      popoverSide="bottom"
      align="start"
      className="w-96"
    >
      <div className="flex items-center gap-1 flex-wrap max-w-full overflow-hidden group relative">
        {primaryPhone ? (
          <TooltipWithContent content={primaryPhone}>
            <Badge
              variant="default"
              className="bg-blue-100 text-blue-800 text-xs py-0.5 px-2 truncate dark:bg-blue-900/40 dark:text-blue-200"
            >
              <Phone className="h-2.5 w-2.5 mr-0.5 flex-shrink-0" />
              <span className="truncate">{primaryPhone}</span>
            </Badge>
          </TooltipWithContent>
        ) : null}
        {aliases.slice(0, 1).map((alias) => (
          <TooltipWithContent key={alias} content={alias}>
            <Badge variant="outline" className="text-xs py-0.5 px-2 truncate max-w-[120px]">
              {alias}
            </Badge>
          </TooltipWithContent>
        ))}
        {aliases.length > 1 && (
          <Badge variant="secondary" className="text-xs py-0.5 px-2">
            +{aliases.length - 1}
          </Badge>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
            onEditChange?.(true);
          }}
          className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 p-1 hover:bg-blue-50 rounded hover:shadow-sm text-gray-400 hover:text-blue-600 dark:hover:bg-blue-950/40"
          title="Click to edit"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>
    </ResponsivePopover>
  );
}
